import { Select as AntSelect } from 'antd'

import styled, { DefaultTheme } from 'styled-components'

import { space } from 'styled-system'

import { SelectProps, SelectVariants } from './Select'

type SizeType = 'small' | 'middle' | 'large' | 'normal'

const selectheights: Record<SizeType, string> = {
  small: '32px',
  normal: '40px',
  middle: '40px',
  large: '56px'
}

function getSelectBackground(variant: SelectVariants | undefined, theme: DefaultTheme) {
  if (!variant) return theme.colors.primary

  return {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    default: '#fff',
    gray: '#fff'
  }[variant]
}

function getSelectBorder(variant: SelectVariants | undefined, theme: DefaultTheme) {
  if (!variant) return theme.colors.primary
  return {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    default: theme.colors.primary,
    gray: theme.colors.borderPrimary
  }[variant]
}

function getSelectColor(variant: SelectVariants | undefined, theme: DefaultTheme) {
  if (!variant) return theme.colors.primary

  return {
    primary: theme.colors.textPrimaryInverse,
    secondary: theme.colors.textPrimaryInverse,
    default: theme.colors.primary,
    gray: theme.colors.textPrimary
  }[variant]
}

function getPlaceholderColor(variant: SelectVariants | undefined, theme: DefaultTheme) {
  if (!variant) return theme.colors.primary

  return {
    primary: theme.colors.textPrimaryInverse,
    secondary: theme.colors.textPrimaryInverse,
    default: theme.colors.primary,
    gray: theme.colors.borderPrimary
  }[variant]
}

function getPlaceholderFontWeight(variant: SelectVariants | undefined) {
  if (!variant) return 'bold'
  if (variant === 'gray') return 'normal'
  return 'bold'
}

function getArrowColor(variant: SelectVariants | undefined, theme: DefaultTheme) {
  if (variant === 'gray') return theme.colors.primary
  return getSelectColor(variant, theme)
}

export const StyledSelect = styled(AntSelect)<SelectProps>`
  ${space}

  &.ant-select {
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    height: ${({ size }) => selectheights[size || 'normal']};
    flex: ${({ $orientation }) => ($orientation === 'horizontal' ? '1' : '0')};

    &:not(.ant-select-disabled):hover .ant-select-selector {
      border: 1px solid ${({ theme, $colorVariant }) => getSelectBorder($colorVariant, theme)};
    }

    .ant-select-selector {
      ${({ mode, size }) =>
        `${mode === 'multiple' || mode === 'tags' ? 'min-height' : 'height'}: ${selectheights[size || 'normal']};`}
      border: 1px solid ${({ theme, $colorVariant }) => getSelectBorder($colorVariant, theme)};
      border-radius: 4px;
      background-color: ${({ theme, $colorVariant }) => getSelectBackground($colorVariant, theme)};
      color: ${({ theme, $colorVariant }) => getSelectColor($colorVariant, theme)};
      padding: 0 14px;

      .ant-select-selection-search {
        ${({ mode }) => (mode === 'multiple' || mode === 'tags' ? '' : 'position: absolute;')}

        .ant-select-selection-search-input {
          height: ${({ size }) => selectheights[size || 'normal']};
          cursor: pointer;
          font-size: 16px;
        }
      }

      .ant-select-selection-item {
        :global {
          align-items: center;
          line-height: ${({ size }) => selectheights[size || 'normal']};
          background-color: ${({ theme, $colorVariant }) => getSelectBackground($colorVariant, theme)};
        }
        font-size: 16px;
        font-weight: ${({ $colorVariant }) => getPlaceholderFontWeight($colorVariant)};
      }

      .ant-select-selection-placeholder {
        opacity: 1;
        height: ${({ size }) => selectheights[size || 'normal']};
        line-height: ${({ size }) => selectheights[size || 'normal']};
        color: ${({ theme, $colorVariant }) => getPlaceholderColor($colorVariant, theme)};
        font-weight: ${({ $colorVariant }) => getPlaceholderFontWeight($colorVariant)};
        font-size: 16px;
        padding-right: 23px;
      }
    }

    .ant-select-arrow {
      color: ${({ theme, $colorVariant }) => getArrowColor($colorVariant, theme)};
    }

    &.ant-select-disabled {
      opacity: 0.7;

      * {
        cursor: not-allowed !important;
      }
    }
  }
`

export const StyledMulticolumnSelect = styled(StyledSelect)`
  &.ant-select {
    min-width: 200px;

    .ant-select-selection-search-input {
      border-radius: 4px;
      background-color: ${({ theme, $colorVariant }) => getSelectBackground($colorVariant, theme)};
      color: ${({ theme, $colorVariant }) => getPlaceholderColor($colorVariant, theme)};
      border: 1px solid ${({ theme, $colorVariant }) => getSelectBorder($colorVariant, theme)};
      outline: 0;
      font-weight: ${({ $colorVariant }) => getPlaceholderFontWeight($colorVariant)};
    }

    .ant-select-selection-item {
      font-weight: ${({ $colorVariant }) => getPlaceholderFontWeight($colorVariant)};
      font-size: 16px !important;
    }
  }
`
